/**
 * The coach schedule grid as data, plus the range-selection maths.
 *
 * The grid used to be built straight into JSX inside the render loop, which
 * meant nothing else could ask questions about it — "which cells sit next to
 * this one and share its state" has no answer if the cells only exist as
 * elements. Marking a span needs exactly that, so the ladder is built here
 * first and rendered second.
 *
 * Everything in this file is pure: no React, no network. That is deliberate —
 * the interval arithmetic is the part that can quietly destroy a coach's day,
 * so it should be checkable on its own.
 */
import { ICoachBooking, ICoachIntervalItem } from "../../types/api";
import { IFacility } from "../../types/facility";

export type SlotState = "available" | "booked" | "unavailable";

export interface IDaySlot {
  /** "HH:MM:SS" */
  startTime: string;
  /** "HH:MM:SS" */
  endTime: string;
  /** "HH:MM - HH:MM", what the cell shows. */
  label: string;
  state: SlotState;
  booking?: ICoachBooking | null;
}

export interface IDayColumn {
  date: Date;
  /** Raw formatToIsoDate output, which the single-slot flow passes onwards. */
  isoDate: string;
  /** "YYYY-MM-DD" — the key the availability records are stored under. */
  day: string;
  /**
   * The facility has no opening hours for this date. Distinct from a column
   * that is empty because everything in it has already passed and is being
   * hidden — the two look identical otherwise, and mean opposite things.
   */
  isClosed: boolean;
  slots: IDaySlot[];
}

const SLOT_MINUTES = 30;

export const toSeconds = (time: string): number => {
  const [h = 0, m = 0, s = 0] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

const pad = (value: number) => String(value).padStart(2, "0");

export const toTimeString = (seconds: number): string =>
  `${pad(Math.floor(seconds / 3600))}:${pad(
    Math.floor((seconds % 3600) / 60)
  )}:${pad(seconds % 60)}`;

/** "09:00:00" -> "09:00" */
export const shortTime = (time: string): string => time.slice(0, 5);

/**
 * Opening and closing hours for one date: special day, then the matching
 * weekday of an active season, then the default weekday hours.
 *
 * Returns nulls when the facility is shut, which the grid renders as an empty
 * column rather than a column of closed cells.
 */
export function resolveDayHours(
  facility: IFacility,
  date: Date,
  isoDate: string
): { openingTime: string | null; closingTime: string | null } {
  const day = isoDate.split("T")[0];
  const weekday = date.toLocaleString("en-US", { weekday: "long" }).toLowerCase();

  const specialDay = facility.special_days?.find((d) => d.date === day);
  if (specialDay)
    return {
      openingTime: specialDay.opening_hours,
      closingTime: specialDay.closing_hours,
    };

  const season = facility.seasonal_hours?.find(
    (period) => day >= period.start_date && day <= period.end_date
  );
  const seasonHours = season?.weekday_hours.find(
    (d) => d.weekday.toLowerCase() === weekday
  );
  if (seasonHours?.opening_hours && seasonHours?.closing_hours)
    return {
      openingTime: seasonHours.opening_hours,
      closingTime: seasonHours.closing_hours,
    };

  const defaultHours = facility.default_hours.find(
    (d) => d.weekday.toLowerCase() === weekday
  );
  return {
    openingTime: defaultHours?.opening_hours ?? null,
    closingTime: defaultHours?.closing_hours ?? null,
  };
}

/**
 * The 30-minute ladder for one day, between the facility's opening and closing
 * hours.
 *
 * A booking collapses to a single cell covering its whole length, which is why
 * the ladder is not a uniform grid and why callers must work in slot indices
 * rather than assuming every cell is half an hour.
 */
export function buildDaySlots(
  openingTime: string,
  closingTime: string,
  intervals: ICoachIntervalItem[],
  /**
   * When given, free and closed cells that have already started are left out
   * altogether. Bookings always stay — what happened is still worth seeing,
   * it is only the availability around it that has stopped being editable.
   *
   * Filtering here rather than at render keeps slot indices contiguous, which
   * the span logic depends on.
   */
  hidePassed?: { day: string; now: Date }
): IDaySlot[] {
  const slots: IDaySlot[] = [];
  const closing = toSeconds(closingTime);
  let cursor = toSeconds(openingTime);

  while (cursor < closing) {
    const booked = intervals.find(
      (interval) =>
        interval.booking &&
        toSeconds(interval.start_time) <= cursor &&
        cursor < toSeconds(interval.end_time)
    );

    if (booked) {
      slots.push({
        startTime: booked.start_time,
        endTime: booked.end_time,
        label: `${shortTime(booked.start_time)} - ${shortTime(booked.end_time)}`,
        state: "booked",
        booking: booked.booking,
      });
      const bookedEnd = toSeconds(booked.end_time);
      // A zero-length or reversed booking would otherwise spin here forever.
      cursor = bookedEnd > cursor ? bookedEnd : cursor + SLOT_MINUTES * 60;
      continue;
    }

    const next = cursor + SLOT_MINUTES * 60;
    const covered = intervals.some(
      (interval) =>
        toSeconds(interval.start_time) <= cursor &&
        cursor < toSeconds(interval.end_time)
    );
    const startTime = toTimeString(cursor);
    const endTime = toTimeString(next);
    const skip =
      hidePassed !== undefined &&
      isTimeInPast(hidePassed.day, startTime, hidePassed.now);

    if (!skip) {
      slots.push({
        startTime,
        endTime,
        label: `${shortTime(startTime)} - ${shortTime(endTime)}`,
        state: covered ? "available" : "unavailable",
      });
    }
    cursor = next;
  }

  return slots;
}

export interface ISlotRun {
  /** Index into the day's slot list, so selection still works in absolutes. */
  from: number;
  to: number;
  state: SlotState;
  startTime: string;
  endTime: string;
  /** "09:00 – 12:00" */
  label: string;
  slots: IDaySlot[];
}

/**
 * Consecutive cells that share a state, as one block each.
 *
 * A phone showing 07:00–23:00 as half-hours is around 1900px of scrolling for
 * what is usually three or four facts ("shut till nine, open till noon, Anna
 * at noon, shut after"). Grouping turns that into three or four blocks.
 *
 * Bookings always stand alone — two back-to-back bookings are two different
 * people, not one run.
 */
export function groupIntoRuns(slots: IDaySlot[]): ISlotRun[] {
  const runs: ISlotRun[] = [];

  slots.forEach((slot, index) => {
    const previous = runs[runs.length - 1];
    const continues =
      previous !== undefined &&
      previous.state === slot.state &&
      slot.state !== "booked";

    if (continues) {
      previous.to = index;
      previous.endTime = slot.endTime;
      previous.label = `${shortTime(previous.startTime)} – ${shortTime(
        slot.endTime
      )}`;
      previous.slots.push(slot);
      return;
    }

    runs.push({
      from: index,
      to: index,
      state: slot.state,
      startTime: slot.startTime,
      endTime: slot.endTime,
      label: `${shortTime(slot.startTime)} – ${shortTime(slot.endTime)}`,
      slots: [slot],
    });
  });

  return runs;
}

/** Whether a cell can take part in a span at all. */
export const isSelectableSlot = (slot: IDaySlot): boolean =>
  slot.state !== "booked";

/**
 * How far a span anchored on `anchor` may run: outwards in both directions
 * while the cells keep the same state.
 *
 * This is what stops a span swallowing a booking, and what stops "open these
 * hours" from also re-opening hours that were already open.
 */
export function selectableBounds(
  slots: IDaySlot[],
  anchor: number
): { first: number; last: number } {
  const state = slots[anchor]?.state;
  let first = anchor;
  let last = anchor;
  if (state === undefined) return { first, last };

  const extends_ = (slot: IDaySlot) =>
    slot.state === state && isSelectableSlot(slot);

  while (first - 1 >= 0 && extends_(slots[first - 1])) first -= 1;
  while (last + 1 < slots.length && extends_(slots[last + 1])) last += 1;
  return { first, last };
}

/**
 * Whether `time` on `day` has already begun.
 *
 * Deliberately not a property of a slot: cells render normally whatever the
 * hour, and this is only asked once, about a span the user has actually
 * selected. Measured against the start, because a member cannot book a time
 * that has already started — so there is nothing left to open or close.
 */
export function isTimeInPast(day: string, time: string, now: Date): boolean {
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}`;
  if (day < today) return true;
  if (day > today) return false;
  return (
    toSeconds(time) <=
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  );
}

export type SpanAction = "open" | "close";

/** What confirming a span anchored on this cell would do. */
export const actionForState = (state: SlotState): SpanAction | null => {
  if (state === "available") return "close";
  if (state === "unavailable") return "open";
  return null;
};

export interface ISpanSummary {
  startTime: string;
  endTime: string;
  /** "09:00–12:00" */
  label: string;
  slotCount: number;
  minutes: number;
}

export function spanSummary(
  slots: IDaySlot[],
  anchor: number,
  focus: number
): ISpanSummary {
  const from = Math.min(anchor, focus);
  const to = Math.max(anchor, focus);
  const startTime = slots[from].startTime;
  const endTime = slots[to].endTime;
  return {
    startTime,
    endTime,
    label: `${shortTime(startTime)}–${shortTime(endTime)}`,
    slotCount: to - from + 1,
    minutes: (toSeconds(endTime) - toSeconds(startTime)) / 60,
  };
}

/**
 * The day's new interval list after opening or closing `[spanStart, spanEnd)`.
 *
 * makeCoachAvailable replaces the whole day, so booked intervals have to be
 * handed back untouched or the bookings are destroyed by the write. They are
 * also never inside a span — selectableBounds cannot cross them — so passing
 * them straight through is both safe and correct.
 */
export function applySpan(
  intervals: ICoachIntervalItem[],
  spanStart: string,
  spanEnd: string,
  action: SpanAction,
  price: string
): ICoachIntervalItem[] {
  const from = toSeconds(spanStart);
  const to = toSeconds(spanEnd);

  const booked = intervals.filter((interval) => interval.booking);
  const free = intervals.filter((interval) => !interval.booking);

  let result: ICoachIntervalItem[];

  if (action === "open") {
    // Absorb anything the new span touches, so opening 10:00–11:00 next to an
    // existing 09:00–10:00 leaves one 09:00–11:00 interval rather than two
    // abutting ones that would then have to be clicked away separately.
    const touching = free.filter(
      (interval) =>
        toSeconds(interval.start_time) <= to &&
        toSeconds(interval.end_time) >= from
    );
    const untouched = free.filter((interval) => !touching.includes(interval));
    const start = Math.min(
      from,
      ...touching.map((interval) => toSeconds(interval.start_time))
    );
    const end = Math.max(
      to,
      ...touching.map((interval) => toSeconds(interval.end_time))
    );
    result = [
      ...untouched,
      { start_time: toTimeString(start), end_time: toTimeString(end), price },
    ];
  } else {
    result = free.flatMap((interval) => {
      const start = toSeconds(interval.start_time);
      const end = toSeconds(interval.end_time);
      if (end <= from || start >= to) return [interval];
      const pieces: ICoachIntervalItem[] = [];
      if (start < from) pieces.push({ ...interval, end_time: toTimeString(from) });
      if (end > to) pieces.push({ ...interval, start_time: toTimeString(to) });
      return pieces;
    });
  }

  return [...booked, ...result].sort(
    (a, b) => toSeconds(a.start_time) - toSeconds(b.start_time)
  );
}
